//@flow

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';
import getter from 'dibs-object-getter';
import scrollIntoView from 'scroll-into-view';
import {
    TPN_TIER_BRONZE,
    TPN_TIER_SILVER,
    TPN_TIER_GOLD,
    TPN_TIER_PLATINUM,
} from 'dibs-trade-rewards-progress/exports/tpnStatusCodes';

import { RewardsDashboardHeader } from './RewardsDashboardHeader';
import RewardsProgressModule from './RewardsProgressModule';
import ProfileStatusModule from './ProfileStatusModule';
import { TpnOverview } from './TpnOverview';
import { BENEFITS_CHART_ID } from '../../loyaltyModules/LoyaltyModulesWrapper';

import styles from './TpnRewardsDashboard.scss';

import { profileStatuses } from '../../constants';
const { ACTIVE, PENDING, DELETED } = profileStatuses;

import type { TpnRewardsDashboard_viewer as Viewer } from './__generated__/TpnRewardsDashboard_viewer.graphql';
import type { TpnRewardsDashboard_user as User } from './__generated__/TpnRewardsDashboard_user.graphql';

const scrollToBenefitsChart = (e?: SyntheticEvent<*>) => {
    // istanbul ignore else
    if (e) {
        e.preventDefault();
    }

    const element = document.querySelector(`#${BENEFITS_CHART_ID}`);

    // istanbul ignore if
    if (element) {
        scrollIntoView(element, {
            align: {
                top: 0,
                topOffset: 100,
            },
            time: 1000,
        });
    }
};

type TpnRewardsDashboardProps = {|
    viewer: Viewer,
    user: User,
    registerDeepLink?: (Element, string) => void,
|};

export function TpnRewardsDashboard({ user, viewer, registerDeepLink }: TpnRewardsDashboardProps) {
    const tradeFirm = getter(user).value('tradeFirm') || {};
    const profileStatus =
        getter(user)
            .at('tradeFirm')
            .value('profileStatus') || DELETED;
    const tpnRole = getter(user)
        .at('tradeFirm')
        .at('rewards')
        .at('currentProgram')
        .at('programInfo')
        .value('code');

    const isTpn = [TPN_TIER_BRONZE, TPN_TIER_SILVER, TPN_TIER_GOLD, TPN_TIER_PLATINUM].includes(
        tpnRole
    );
    const showNoTpnBanner = profileStatus !== ACTIVE && profileStatus !== PENDING && !isTpn;
    const bottomModulesAdditionalClass = showNoTpnBanner ? styles.wideTopBar : '';

    return (
        <div className={styles.wrapper}>
            <RewardsDashboardHeader tradeFirm={(tradeFirm: Object)} />
            <div className={styles.TpnOverviewWrapper}>
                <TpnOverview
                    scrollToBenefitsChart={scrollToBenefitsChart}
                    tradeFirm={(tradeFirm: Object)}
                    viewer={(viewer: Object)}
                />
            </div>
            <div className={styles.moduleRow}>
                <RewardsProgressModule
                    registerDeepLink={registerDeepLink}
                    scrollToBenefitsChart={scrollToBenefitsChart}
                    tradeFirm={(tradeFirm: Object)}
                    wrapperClass={classnames(
                        styles.rewardsProgressWrapper,
                        bottomModulesAdditionalClass
                    )}
                />
                <ProfileStatusModule
                    tradeFirm={(tradeFirm: Object)}
                    className={classnames(
                        styles.profileStatusWrapper,
                        bottomModulesAdditionalClass
                    )}
                />
            </div>
        </div>
    );
}

export default createFragmentContainer(TpnRewardsDashboard, {
    viewer: graphql`
        fragment TpnRewardsDashboard_viewer on Viewer {
            ...TpnOverview_viewer
        }
    `,
    user: graphql`
        fragment TpnRewardsDashboard_user on User {
            tradeFirm {
                profileStatus
                rewards {
                    currentProgram {
                        programInfo {
                            code
                        }
                    }
                }
                ...RewardsDashboardHeader_tradeFirm
                ...RewardsProgressModule_tradeFirm
                ...ProfileStatusModule_tradeFirm
                ...TpnOverview_tradeFirm
            }
        }
    `,
});
